/**
 * FR-BILL-043 — P&L unprofitability check for bill-level discounts.
 *
 * Computes which line items would become unprofitable (sold below cost)
 * after a proposed bill-level discount is pro-rated across them.
 *
 * The pro-ration logic mirrors `useBillCalculations` (use-bill-calculations.ts
 * lines 100-117) exactly: weight-based distribution with floor rounding and
 * residual correction to the first non-zero-weight line. If one changes,
 * the other must be updated to match.
 */

import type { CartLineItem } from '@/stores/use-cart-store';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UnprofitableLineResult {
  name: string;
  sku: string;
  /** Positive paise value representing the absolute loss on this line. */
  lossAmount: number;
}

type MarginLineInput = Pick<
  CartLineItem,
  'name' | 'sku' | 'unitCost' | 'quantity' | 'lineSubtotal' | 'discountAmount'
>;

// ─── Core ───────────────────────────────────────────────────────────────────

/**
 * Returns line items that would become unprofitable (negative margin) if
 * the proposed bill-level discount were applied and pro-rated.
 *
 * Items with `unitCost === 0` are excluded — without cost data we cannot
 * determine profitability.
 */
export function computeUnprofitableLines(
  items: MarginLineInput[],
  discountType: 'PERCENTAGE' | 'FLAT',
  discountValue: number,
): UnprofitableLineResult[] {
  if (items.length === 0 || discountValue <= 0) return [];

  // Taxable base = sum of per-line (subtotal - line discount).
  const taxableBase = items.reduce(
    (sum, i) => sum + (i.lineSubtotal - i.discountAmount),
    0,
  );
  if (taxableBase <= 0) return [];

  // ── Bill discount amount (mirrors use-bill-calculations.ts:28-40) ──
  let totalBillDiscount: number;
  if (discountType === 'PERCENTAGE') {
    const clampedPct = Math.max(0, Math.min(100, discountValue));
    totalBillDiscount = Math.floor((taxableBase * clampedPct) / 100);
  } else {
    totalBillDiscount = Math.min(Math.max(0, Math.floor(discountValue)), taxableBase);
  }
  if (totalBillDiscount <= 0) return [];

  // ── Pro-rate (mirrors use-bill-calculations.ts:100-117) ────────────
  const perLineShare = items.map((i) => {
    const w = i.lineSubtotal - i.discountAmount;
    if (w <= 0) return 0;
    return Math.floor((totalBillDiscount * w) / taxableBase);
  });

  const allocated = perLineShare.reduce((s, v) => s + v, 0);
  const residual = totalBillDiscount - allocated;
  if (residual > 0) {
    for (let idx = 0; idx < items.length; idx++) {
      if (items[idx].lineSubtotal - items[idx].discountAmount > 0) {
        perLineShare[idx] += residual;
        break;
      }
    }
  }

  // ── Margin check per line ──────────────────────────────────────────
  const result: UnprofitableLineResult[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.unitCost === 0) continue; // no cost data

    const revenue = item.lineSubtotal - item.discountAmount - perLineShare[i];
    const cost = item.unitCost * item.quantity;
    const margin = revenue - cost;

    if (margin < 0) {
      result.push({
        name: item.name,
        sku: item.sku,
        lossAmount: Math.abs(margin),
      });
    }
  }

  return result;
}
